import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { listBdcStores } from "../../../services";
import { BdcStore } from "../../../types/bdc.type";
import { useDebounce } from "../../../utils/hooks/useDebounce";
import { lineText, pcs, thaiDate } from "../format";
import { SCROLL_SX } from "../styles";
import { useBdcDcs } from "../useBdcDcs";

const ALL = "all";

/** ร้าน 7-11 — read-only; created and moved by import from PICKLST. */
export default function StoreTab() {
  const { dcs } = useBdcDcs();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search.trim(), 400);
  const [dcCode, setDcCode] = useState(ALL);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [rows, setRows] = useState<BdcStore[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [debounced, dcCode, limit]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcStores({
        search: debounced || undefined,
        dc_code: dcCode === ALL ? undefined : dcCode,
        page: page + 1,
        limit,
      });
      setRows(data?.rows ?? []);
      setCount(data?.count ?? 0);
    } catch {
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [debounced, dcCode, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1.25 }}>
        <TextField
          size="small"
          placeholder="รหัสร้าน / ชื่อร้าน"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField select size="small" label="DC" value={dcCode} onChange={(e) => setDcCode(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value={ALL}>ทุก DC</MenuItem>
          {dcs.map((dc) => (
            <MenuItem key={dc.dc_code} value={dc.dc_code}>
              {dc.dc_code} {dc.dc_name}
            </MenuItem>
          ))}
        </TextField>
        <Typography className="sub">
          {pcs(count)} ร้าน · สร้างและอัปเดตจาก PICKLST อัตโนมัติ (รหัส, ชื่อ, DC, รอบ, สาย, จุดส่ง)
        </Typography>
      </Stack>
      {loading && <LinearProgress />}
      <Box sx={SCROLL_SX}>
        <table className="bdc-t">
          <thead>
            <tr>
              <th>รหัสร้าน</th>
              <th>ชื่อร้าน</th>
              <th>DC</th>
              <th className="n">รอบ</th>
              <th className="n">สาย</th>
              <th className="n">จุดส่ง</th>
              <th>เห็นครั้งแรก</th>
              <th>สั่งล่าสุด</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} className="c sub">
                  ไม่พบร้าน
                </td>
              </tr>
            ) : (
              rows.map((store) => (
                <tr key={store.store_code}>
                  <td className="mono">{store.store_code}</td>
                  <td>{store.store_name}</td>
                  <td>{store.dc_code}</td>
                  <td className="n">{store.round ?? "-"}</td>
                  <td className="n">{lineText(store.line_no)}</td>
                  <td className="n">{lineText(store.stop_no)}</td>
                  <td className="sub">{thaiDate(store.first_seen_date)}</td>
                  <td className="sub">{thaiDate(store.last_seen_date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Box>
      <TablePagination
        component="div"
        count={count}
        page={page}
        rowsPerPage={limit}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_e, next) => setPage(next)}
        onRowsPerPageChange={(e) => setLimit(Number(e.target.value))}
        labelRowsPerPage="แถวต่อหน้า"
        labelDisplayedRows={({ from, to, count: total }) => `${from}-${to} จาก ${pcs(total)}`}
      />
    </Box>
  );
}
